// core
import {
  Component, OnInit, Input, Inject, ViewChild, ElementRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// campfire
import { PartnerBrandService } from '@campfire-nx/api';

// constants
import { RAPTOR_DEBOUNCE_TIME, RAPTOR_STEP_ENTER_TIME } from '../../../constant/raptor-constants';

// services
import { RaptorDataService } from '../../../services/raptor-data.service';
import { RaptorStepService } from '../../../services/raptor-step.service';
import { RaptorAnalyticsService } from '../../../services/raptor-analytics.service';
import { RaptorStepDataService } from '../../../services/raptor-step-data.service';

@Component({
  selector: 'open-raptor-page-axis-travel-covers',
  templateUrl: './raptor-page-axis-travel-covers.component.html',
  styleUrls: ['./raptor-page-axis-travel-covers.component.scss']
})
export class RaptorPageAxisTravelCoversComponent implements OnInit {
  @Input() formData: any;
  @Input() form!: FormGroup;

  @ViewChild('marketing') marketing!: ElementRef<HTMLInputElement>;
  marketingType!: string; // the type of marketing content being shown
  marketingInfo!: any;
  marketingEvent!: any;
  windowScrollEvent!: any;
  priceScrollEnd!: any;
  priceScroll!: any;
  priceResize!: any;
  backgroundCountries = ['NZL', 'IDN', 'IDN_BAL', 'USA', 'GBR', 'THA', 'IND', 'FJI', 'SGP', 'JPN', 'VNM', 'PHL', 'ITA', 'MYS', 'CAN', 'FRA', 'DEU', 'ESP', 'GRC', 'IRL', 'NLD', 'PRT'];
  public stylesheet: SafeHtml = '';
  currency!: string;
  countryCode!: string;

  layoutSettings: any = {};
  bannerHeaders?: any;
  frequencyControl!: any;
  coverLevelQuestion!: any;
  coverLevelControl: any;
  coverTypeControl: any;
  covers!: Array<any>;
  coverLevel!: any;
  coverType!: any;
  productSlugControl: any;
  disclaimers?: Array<any>;
  includedExtrasDisclaimers?: Array<any>;
  panelPosition = 0; // remember what panel is selected
  panelOpen: any; // the currently opend accordian panel

  constructor(
    public raptorStepService: RaptorStepService,
    private raptorAnalyticsService: RaptorAnalyticsService,
    private raptorStepDataService: RaptorStepDataService,
    private raptorDataService: RaptorDataService,
    private partnerBrandService: PartnerBrandService,
    private domSanitizer: DomSanitizer,
    @Inject('environment') private environment: any
  ) { }

  ngOnInit(): void {
    this.layoutSettings = this.formData.settings || {};
    this.coverLevelControl = this.form.get('cover_level');
    this.coverTypeControl = this.form.get('cover_type');
    this.productSlugControl = this.form.get('product_slug');
    this.frequencyControl = this.form.get('frequency');
    this.coverLevel = this.formData.data.cover_level;
    this.coverType = this.formData.data.cover_type;
    this.filterCovers();
    this.getCountryBackground();
    this.currency = this.partnerBrandService.getCurrencySymbol();
    this.countryCode = this.partnerBrandService.getCountryCode('country_alpha2');

    if (this.formData.settings.marketing) {
      this.marketingInfo = this.formData.settings.marketing[this.coverType] || this.formData.settings.marketing.default;
    }
  }

  ngAfterViewInit(): void {
    this.windowScrollEvent = window.addEventListener('scroll', this.pinCovers);

    setTimeout(() => {
      if (this.marketing) {
        this.marketingEvent = this.marketing.nativeElement.addEventListener('click', (event: any) => {
          if (this.marketingInfo.content && this.marketingInfo.content.length) {
            this.marketingInfo.content.forEach((content: any) => {
              if (content.tooltips && content.tooltips.length) {
                content.tooltips.forEach((tooltip: any) => {
                  if (event.target.className === tooltip.key) {
                    const tooltipTrigger: HTMLElement = document.querySelector(`button[title="${tooltip.key}"]`) as HTMLElement;
                    if (tooltipTrigger) {
                      tooltipTrigger.click();
                      event.preventDefault();
                    }
                  }
                });
              }
            });
          }
        });
      }

      // carousel controls scroll listener
      const priceContainer = document.getElementsByClassName('raptor-page-price-container')[0];
      if (priceContainer) {
        this.priceScrollEnd = priceContainer.addEventListener('scrollend', this.priceScroller);
        this.priceScroll = priceContainer.addEventListener('scroll', this.pinCovers);
        this.priceResize = window.addEventListener('resize', this.setControls);

        setTimeout(() => {
          // if there is a selected then set the carousel open value
          this.covers.forEach((cover: any, index: number) => {
            if (
              cover.cover.product_slug === this.formData.data.product_slug &&
              cover.cover.cover_type === this.formData.data.cover_type &&
              cover.cover.cover_level === this.formData.data.cover_level
            ) {
              this.panelPosition = index;
            }
          });

          if (this.panelPosition > 0) {
            const position = this.getPanelScrollposition(this.panelPosition);
            priceContainer.scrollLeft = position;
          }
        }, RAPTOR_STEP_ENTER_TIME);
      }
    }, RAPTOR_DEBOUNCE_TIME);
  }

  ngOnDestroy(): void {
    if (this.marketing) {
      this.marketing.nativeElement.removeEventListener('click', this.marketingEvent);
    }
    const priceContainer = document.getElementsByClassName('raptor-page-price-container')[0];
    if (priceContainer) {
      priceContainer.removeEventListener('scrollend', this.priceScroller);
    }
    if (this.windowScrollEvent) {
      this.windowScrollEvent.removeListener('scroll', this.pinCovers);
    }
    if (this.priceScrollEnd) {
      priceContainer.removeEventListener('scrollend', this.priceScroller);
    }
    if (this.priceScroll) {
      priceContainer.removeEventListener('scroll', this.pinCovers);
    }
    if (this.priceResize) {
      window.removeEventListener('resize', this.setControls);
    }
  }

  filterCovers() {
    this.covers = this.formData.covers.filter((cover: any) => cover.cover.cover_type === this.formData.data.cover_type);
    this.coverLevelControl.setValue(this.coverLevel, { emitEvent: false });
    this.getBanners(this.covers);

    // update filtered covers to include PAF banners if available
    this.covers = this.covers.map((cover: any) => {
      // if monthly has arrangement fee, so does annual by default
      const hasArrangFeeExtra = cover.premium.U.extras?.arrang_fee?.charge_upfront ?? false;

      if (cover.has_additional_charges && hasArrangFeeExtra) {
        cover.additionalChargesBanners = this.getPolicyArrangementBannerDisclaimer(cover, this.disclaimers);
      }

      return cover;
    });

    this.panelOpen = [];
    // set up the accorion control object
    this.covers.forEach((cover: any) => {
      const features = this.getCoverInfo(cover, 'features');
      const panelArray: Array<boolean> = [];
      if (features) {
        features.forEach(() => {
          panelArray.push(false);
        });
        this.panelOpen.push(panelArray);
      }
    });
  }

  selectCover(cover: any) {
    this.coverLevelControl.setValue(cover.cover.cover_level, { emitEvent: false });
    this.productSlugControl.setValue(cover.cover.product_slug, { emitEvent: false });
    setTimeout(() => {
      const form = document.getElementById('coverSubmit');
      form?.click();
    }, 500);
  }

  getCover(cover: any): any {
    let formData = this.raptorStepDataService.get();
    return formData.covers.find((item: any) => {
      return item.cover.product_slug === cover.cover.product_slug &&
        item.cover.cover_type === cover.cover.cover_type &&
        item.cover.cover_level === cover.cover.cover_level;
    });
  }

  getCoverPrice(cover: any): any {
    // the covers object can change, so get the latest price
    let formData = this.raptorStepDataService.get();
    let updatedCover = this.getCover(cover);
    let formValues = this.form.value;
    let price = {
      currency: this.currency,
      amount: 0
    };
    let frequency = formValues.frequency || formData.data.frequency;
    let premiumAttr = 'payable';

    if (!frequency) {
      frequency = 'M';
    }

    if (frequency === 'U') {
      premiumAttr = 'payable';
    }
    if (frequency === 'M') {
      premiumAttr = 'payable_monthly';
    }
    if (this.layoutSettings.premium_attribute === 'payable') {
      premiumAttr = 'payable';
    }

    if (updatedCover && updatedCover.premium) {
      price.amount = updatedCover.premium[frequency] && updatedCover.premium[frequency][premiumAttr];
    }

    return price;
  }

  getYearlyCoverPrice(cover: any): any {
    let updatedCover = this.getCover(cover);
    let price = {
      currency: this.currency,
      amount: 0,
      frequency: 'year'
    };

    price.amount = updatedCover.premium.M && updatedCover.premium.M.payable;
    price.amount = Math.ceil(price.amount);
    return price;
  }

  getCoverDiscount(cover: any): any {
    let formData = this.raptorStepDataService.get();
    let updatedCover = this.getCover(cover);
    let formValues = this.form.value;
    let frequency = formValues['frequency'] || formData.data['frequency'];
    let discount;

    if (
      updatedCover &&
      updatedCover.premium &&
      updatedCover.premium[frequency] &&
      updatedCover.premium[frequency].prediscount_payable
    ) {
      discount = {
        currency: this.currency,
        amount: updatedCover.premium[frequency] && updatedCover.premium[frequency].prediscount_payable
      }; 
    }
    return discount;
  }


  getCoverFeatures(cover: any): any {
    let updatedCover = this.getCover(cover);
    return updatedCover.features;
  }

  getCoverInfo(cover: any, property: string): any {
    const feature = cover.is_featured ? 'featured' : 'regular';
    let coverInfo!: any;
    let info = '';
    

    if (
      this.formData.settings.details &&
      this.formData.settings.details[feature] &&
      this.formData.settings.details[feature][cover.cover.cover_type] &&
      this.formData.settings.details[feature][cover.cover.cover_type][cover.cover.cover_level]
    ) {
      coverInfo = this.formData.settings.details[feature][cover.cover.cover_type][cover.cover.cover_level];
    }

    if (coverInfo && coverInfo[property]) {
      info = coverInfo[property];
    }
    return info;
  }

  getBanners(covers: any) {
    this.disclaimers = this.raptorDataService.mapBannerDisclaimers(covers);

    if (this.formData.banners) {
      this.bannerHeaders = this.formData.banners.filter((banner: any) => banner.type === 'header');
      this.bannerHeaders.forEach((banner: any) => this.disclaimers?.push({ type: 'banner', content: banner.disclaimer }));
    }
  }

  getPolicyArrangementBannerDisclaimer(cover: any, disclaimers: any): any {
    let formData = this.raptorStepDataService.get();
    let updatedCover = this.getCover(cover);
    let formValues = this.form.value;
    let frequency = formValues['frequency'] || formData.data['frequency'];
    let banner;

    banner = this.raptorDataService.getArrangementFeeBanner(updatedCover.premium, disclaimers as [], frequency, true);

    // set disclaimers in the footer
    this.includedExtrasDisclaimers = this.raptorDataService.getIncludedExtrasDisclaimers(updatedCover.premium[frequency].extras);

    return banner;
  }

  getTagLabel(banner: any) {
    return this.raptorDataService.mapBannerTag(banner, this.disclaimers);
  }

  openPdsDocument(url: string) {
    this.raptorAnalyticsService.trackAdditionalEvents('select-option', 'pds');
    window.open(`${this.environment.apiEndpoint}${url}`, '_blank');
  }

  navigateCovers(direction: string) {
    if (direction === 'next') {
      const panels = document.getElementsByClassName('raptor-page-price-panel').length;
      this.panelPosition = this.panelPosition < panels - 1 ? this.panelPosition + 1 : this.panelPosition;
    } else if (direction === 'previous') {
      this.panelPosition = this.panelPosition > 0 ? this.panelPosition - 1 : 0;
    }
    this.scrollCovers(this.panelPosition);
  }

  scrollCovers(index: number) {
    const priceContainers = document.getElementsByClassName('raptor-page-price-container');
    let priceContainer: any;
    const position = this.getPanelScrollposition(index);

    if (priceContainers && priceContainers.length) {
      priceContainer = priceContainers[0];

      priceContainer.scrollTo({
        left: position,
        behavior: 'smooth'
      });
    }
  }

  priceScroller = () => {
    const priceContainer = document.getElementsByClassName('raptor-page-price-container')[0];
    const containerPosition = priceContainer.getBoundingClientRect();
    const pricePanels = document.getElementsByClassName('raptor-page-price-panel');
    const panels = Array.from(pricePanels);

    if (panels.length) {
      const offsetPositions: Array<number> = [];
      let targetPosition: any;
      let targetPanel: any;
      const currentScrollPosition = priceContainer.scrollLeft;

      panels.forEach((panel: any, index: number) => {
        const panelPosition = panel.getBoundingClientRect();
        let distanceToScrollPosition;

        if (index === 0) {
          // first panel scroll boundary is on the left of the panel
          distanceToScrollPosition = panelPosition.x + 0;
        } else if (index === panels.length - 1) {
          // last panel scroll boundary is on the right of the panel
          distanceToScrollPosition = (panelPosition.x + panelPosition.width) - containerPosition.width;
        } else {
          // middle panels scroll boudaries are in the middle of the panel
          distanceToScrollPosition = (panelPosition.x + (panelPosition.width / 2)) - (containerPosition.width / 2);
        }
        offsetPositions.push(distanceToScrollPosition);
      });

      // find the panel with the lowest position and then
      // scroll the amount of pixels the current scroll position is off by
      offsetPositions.forEach((position: number, index: number) => {
        if (!targetPosition && targetPosition !== 0) {
          targetPosition = position;
          targetPanel = index;
        } else if (Math.abs(position) < Math.abs(targetPosition)) {
          targetPosition = position;
          targetPanel = index;
        }
      });

      this.panelPosition = targetPanel;

      // prevent half pixel scrolling to prevent looping by making sure target position is greater than 1
      if (Math.abs(targetPosition) > 1) {
        priceContainer.scrollTo({
          left: currentScrollPosition + targetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  getPanelScrollposition(index: number): number {
    const priceContainers = document.getElementsByClassName('raptor-page-price-container');
    let priceContainer: any;
    let containerPosition: any;
    const pricePanels = document.getElementsByClassName('raptor-page-price-panel');
    let panelPosition: any;
    let targetPosition = 0;

    if (priceContainers && priceContainers.length) {
      priceContainer = priceContainers[0];
    }

    if (priceContainer) {
      containerPosition = priceContainer.getBoundingClientRect();
    }
    if (pricePanels[index]) {
      panelPosition = pricePanels[index].getBoundingClientRect();
    }

    if (index === 0) {
      // first panel scroll boundary is on the left of the panel
      targetPosition = 0;
    } else if (index === pricePanels.length - 1 && panelPosition) {
      // last panel scroll boundary is on the right of the panel
      targetPosition = panelPosition.x + panelPosition.width;
    } else if (panelPosition && containerPosition && priceContainer) {
      // middle panels scroll boudaries are in the middle of the panel
      targetPosition = (panelPosition.x + (panelPosition.width / 2)) - (containerPosition.width / 2) + priceContainer.scrollLeft;
    }
    return targetPosition;
  }

  accordionOpend(featureIndex: number) {
    this.panelOpen.forEach((cover: any, coverIndex: number) => {
      cover.forEach((item: any, itemIndex: number) => {
        if (itemIndex === featureIndex) {
          this.panelOpen[coverIndex][itemIndex] = true;
        } else {
          this.panelOpen[coverIndex][itemIndex] = false;
        }
      });
    });
  }

  accordionClosed(featureIndex: number) {
    this.panelOpen.forEach((cover: any, coverIndex: number) => {
      cover.forEach((item: any, itemIndex: number) => {
        if (itemIndex === featureIndex) {
          this.panelOpen[coverIndex][itemIndex] = false;
        }
      });
    });
  }

  pinCovers() {
    const coverPanel = document.getElementsByClassName('raptor-page-price-panel');
    const extrasPanel = document.getElementsByClassName('raptor-page-price-container-extras');
    const highlightContainer = document.getElementsByClassName('raptor-page-price-highlight');
    let containers: any;
    const controls = document.getElementsByClassName('carousel-controls');
    let carouselControls: any;
    let containerPosition: any;

    if (highlightContainer && highlightContainer.length) {
      containers = Array.from(highlightContainer);
      containerPosition = highlightContainer[0].getBoundingClientRect();
    }
    if (controls && controls.length > 1) {
      carouselControls = controls[1];
    }

    if (containers && containers.length) {
      if (containerPosition && containerPosition.top < 0) {
        containers.forEach((element: any, index: number) => {
          let coverPanelDimensions: any;
          let extrasPanelDimensions: any;
          let containerDimensions: any;
          let panel: any;
          let panelDimensions: any;
          let panelContiner: any;

          if (coverPanel[index]) {
            coverPanelDimensions = coverPanel[index].getBoundingClientRect();
          }
          if (extrasPanel.length && extrasPanel[index]) {
            extrasPanelDimensions = extrasPanel[index].getBoundingClientRect();
          }
          if (highlightContainer[index]) {
            containerDimensions = highlightContainer[index].getBoundingClientRect();
          }
          if (highlightContainer[index]) {
            panel = Array.from(highlightContainer[index].getElementsByClassName('raptor-page-price-highlight-panel') as HTMLCollectionOf<HTMLElement>)[0];
          }
          if (panel) {
            panelDimensions = panel.getBoundingClientRect();
          }
          panelContiner = element as HTMLElement;

          if (containerPosition.top < -coverPanelDimensions.height + panelDimensions.height + (extrasPanelDimensions ? extrasPanelDimensions.height : 0) + 2) {
            // stick the price panel to the bottom of the cover panel
            element.classList.add('fixed');
            if (carouselControls) {
              carouselControls.classList.add('fixed');
            }
            panelContiner.style.paddingTop = `${panelDimensions.height}px`;
            panel.style.top = `${coverPanelDimensions.top + coverPanelDimensions.height - panelDimensions.height - 2}px`;
            panel.style.left = `${containerDimensions.left}px`;
            panel.style.right = `${containerDimensions.right}px`;
            panel.style.width = `${containerDimensions.width}px`;
          } else {
            element.classList.add('fixed');
            if (carouselControls) {
              carouselControls.classList.add('fixed');
            }
            panelContiner.style.paddingTop = `${panelDimensions.height}px`;
            panel.style.top = '0px';
            panel.style.left = `${containerDimensions.left}px`;
            panel.style.right = `${containerDimensions.right}px`;
            panel.style.width = `${containerDimensions.width}px`;
          }
        });
      } else {
        containers.forEach((element: any, index: number) => {
          let panel: any;
          const panelContiner = element as HTMLElement;

          if (highlightContainer[index]) {
            panel = Array.from(highlightContainer[index].getElementsByClassName('raptor-page-price-highlight-panel') as HTMLCollectionOf<HTMLElement>)[0];
          }

          element.classList.remove('fixed');
          if (carouselControls) {
            carouselControls.classList.remove('fixed');
          }
          if (panelContiner) {
            panelContiner.style.paddingTop = '0px';
          }
          if (panel) {
            panel.style.left = '0px';
            panel.style.right = '0px';
            panel.style.width = 'auto';
          }
        });
      }
    }
  }

  setControls = () => {
    const priceContainers = document.getElementsByClassName('raptor-page-price-container');
    let priceContainer: any;

    if (priceContainers && priceContainers.length) {
      priceContainer = priceContainers[0];
    }
    if (priceContainer && priceContainer.scrollLeft === 0) {
      this.panelPosition = 0;
    }
  };

  getCountryBackground() {
    if (
      this.formData.data &&
      this.formData.data.travel_destination &&
      this.formData.data.travel_destination.length === 1 &&
      this.backgroundCountries.indexOf(this.formData.data.travel_destination[0]) > -1
    ) {
      let countryCode = this.formData.data.travel_destination[0];
      if (countryCode === 'IDN_BAL') {
        countryCode = 'IDN';
      }
      this.updateStyle(countryCode);
    }
  }

  updateStyle(countryCode: String) {
    this.stylesheet = this.domSanitizer
      .bypassSecurityTrustHtml(
        `<style>
          .raptor-page-container.raptor-page-container {
            background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.80) 0%, rgba(255, 255, 255, 0.00) 100%), url("/assets/partners/huddle/images/Huddle_travel_destination_${countryCode}_image_desktop.webp");
          
            @media screen and (max-width: 639px) {
              background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.80) 0%, rgba(255, 255, 255, 0.00) 100%), url("/assets/partners/huddle/images/Huddle_travel_destination_${countryCode}_image_tablet.webp");
            }
          
            @media screen and (max-width: 479px) {
              background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.80) 0%, rgba(255, 255, 255, 0.00) 100%), url("/assets/partners/huddle/images/Huddle_travel_destination_${countryCode}_image_mobile.webp");
            }
          }
        </style>`
      );
    
  }

  getCoversGstCopy() {
    if (this.countryCode === 'GB') {
      return '';
    } else {
      return 'Including GST and Government charges';
    }
  }
  
  hasFeatureHighlight(cover: any): boolean {
    return cover.features.some((feature: any) => feature.hasOwnProperty('feature_highlight'));
  }

  hasFeaturedCovers(covers:any): boolean {
    return covers.some((cover: any) => this.hasFeatured(cover));
  }

  hasFeatured(cover: any): boolean {    
    return cover.is_featured && cover.extras.flight_delay || this.hasFeatureHighlight(cover);
  }
}
